import { CartUpdatedEvent } from '@theme/events';
import { formatMoney } from '@theme/utilities';

class CrossSellComponent extends HTMLElement {
  constructor() {
    super();

    this.selects = null;
    this.form = null;
    this.formInputs = [];
    this.submitButton = null;
    this.isInCart = false;
    this.isInASlider = false;
    this.productHandle = null;
    this.syncVariant = false;
    this.crossSellVariants = [];
    this.currentVariantId = null;
  }

  connectedCallback() {
    this.selects = this.querySelectorAll('[data-ref="variant-select"]');
    this.form = this.querySelector('[data-ref="add-to-cart-form"]');
    this.formInputs = this.querySelectorAll('[data-ref="product-input"]');
    this.submitButton = this.querySelector('[data-ref="add-to-cart-button"]');
    this.isInCart = this.closest('cart-drawer') || this.closest('cart-component') ? true : false;
    this.isInASlider = this.closest('slider-component') ? true : false;
    this.productHandle = this.dataset.productHandle;
    this.syncVariant = this.dataset.syncVariant === 'true';

    if (this.syncVariant && this.dataset.crossSellVariants) {
      try {
        this.crossSellVariants = JSON.parse(this.dataset.crossSellVariants);
      } catch (e) {
        console.error('Error parsing cross-sell variants:', e);
        this.crossSellVariants = [];
      }
    }

    this.submitButton.addEventListener('click', this.#handleSubmit);

    this.selects.forEach((select) => {
      select.addEventListener('change', this.#handleSelectChange);
    });

    if (this.isInCart) this.#synchronizeWithCartContent();

    if (this.syncVariant) {
      this.#initializeVariantSync();
      document.addEventListener('variant:updated', this.#handleMainProductVariantChange);
    }
  }

  disconnectedCallback() {
    this.submitButton.removeEventListener('click', this.#handleSubmit);

    this.selects.forEach((select) => {
      select.removeEventListener('change', this.#handleSelectChange);
    });

    if (this.syncVariant) {
      document.removeEventListener('variant:updated', this.#handleMainProductVariantChange);
    }
  }

  #handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.submitButton.classList.add('is-loading');

    let items = [];

    if (this.syncVariant && this.currentVariantId) {
      items.push({
        id: this.currentVariantId,
        quantity: 1,
      });
    } else {
      this.selects.forEach((select) => {
        items.push({
          id: select.value,
          quantity: 1,
        });
      });
    }

    const sections = [];
    if (document.querySelector('cart-drawer')) {
      const section_cart_drawer_id = document.querySelector('cart-drawer').dataset.sectionId;
      sections.push(section_cart_drawer_id);
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items,
          sections: sections.join(','),
        }),
      });

      const data = await response.json();

      this.updateCart(data, items.length);

      this.submitButton.classList.remove('is-loading');
    } catch (error) {
      console.error('Error:', error);
    }
  };

  #handleSelectChange = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.submitButton.disabled = true;
    this.updateBundlePrices();

    this.submitButton.disabled = false;

    if (this.dataset.dynamicVariantImage === 'true') {
      this.updateImage();
    }
  };

  updateCart(data, itemsAdded) {
    if (window.location.pathname.includes('/cart')) {
      window.location.reload();
    } else {
      const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();

      let newItemCount;
      if (currentItemCount != null) {
        newItemCount = currentItemCount + itemsAdded;
      }

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: newItemCount,
            source: 'cross-sell',
            sections: data.sections,
          },
        ),
      );
    }
  }

  updateBundlePrices() { 
    const priceContainer = this.querySelector('.price-container .price');
    const compareAtPriceContainer = this.querySelector('.price-container .compare-at-price');

    let price = 0;
    let compareAtPrice = 0;

    this.selects.forEach((select) => {
      const selectedOption = select.options[select.selectedIndex];

      if (selectedOption != null) {
        price += parseInt(selectedOption.dataset.price);
        compareAtPrice += parseInt(selectedOption.dataset.compareAtPrice) || parseInt(selectedOption.dataset.price);
      } else {
        price += parseInt(select.dataset.price);
        compareAtPrice += parseInt(select.dataset.compareAtPrice) || parseInt(select.dataset.price);
      }
    });

    if (priceContainer) {
      priceContainer.innerText = formatMoney(price);
    }

    if (compareAtPriceContainer) {
      if (compareAtPrice > price) {
        compareAtPriceContainer.innerText = formatMoney(compareAtPrice);
      } else {
        compareAtPriceContainer.innerText = '';
      }
    }

    this.#updateButtonPrices(price, compareAtPrice);
  }

  updateImage() {
    const image = this.querySelector('[data-ref="cross-sell-image"]');
    if (!image) return;

    const selectedOption = this.selects[0].options[this.selects[0].selectedIndex];
    if (!selectedOption.dataset.image) return;

    image.src = selectedOption.dataset.image;
  }

  // Si le cross-sell est dans un panier, et que le produit est dans le panier, on masque le cross-sell
  #synchronizeWithCartContent() {
    let cartItems = null;
    if (this.closest('cart-drawer')) cartItems = this.closest('cart-drawer').querySelectorAll('[data-ref="cart-item"]');
    if (this.closest('cart-component')) cartItems = this.closest('cart-component').querySelectorAll('[data-ref="cart-item"]');

    if (!cartItems) return;

    const closestSlider = this.closest('slider-component');

    cartItems.forEach((cartItem) => {
      if (cartItem.dataset.productHandle === this.productHandle) {
        if (this.isInASlider) {
          closestSlider.querySelectorAll('.splide__slide').forEach((slide, index) => {
            if (slide == this.closest('.splide__slide')) {
              closestSlider.remove(index);
            }
          });
        } else {
          this.classList.add('hidden');
        }
      }
    });
  }

  // Initialise la synchronisation des variantes au chargement
  #initializeVariantSync() {
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) return;

    const variantScript = variantPicker.querySelector('script[type="application/json"]');
    if (!variantScript) return;

    try {
      const variantData = JSON.parse(variantScript.textContent);
      const currentVariant = variantData;

      if (!currentVariant || !currentVariant.title) return;

      const matchingVariant = this.crossSellVariants.find((v) => v.title === currentVariant.title);
      if (matchingVariant && matchingVariant.available) {
        this.#updateCrossSellVariant(matchingVariant);
        this.#setCrossSellVisibility(true);
      } else if (matchingVariant && matchingVariant.available === false) {
        this.currentVariantId = null;
        this.#setCrossSellVisibility(false);
      } else {
        this.currentVariantId = null;
        this.#setCrossSellVisibility(true);
      }
    } catch (e) {
      console.error('Error parsing variant data:', e);
    }
  }

  // Gère le changement de variante du produit principal
  #handleMainProductVariantChange = (event) => {
    if (!event.detail?.resource?.title) return;

    const mainVariant = event.detail.resource;
    const matchingVariant = this.crossSellVariants.find((v) => v.title === mainVariant.title);

    if (matchingVariant && matchingVariant.available) {
      this.#updateCrossSellVariant(matchingVariant);
      this.#setCrossSellVisibility(true);
    } else if (matchingVariant && matchingVariant.available === false) {
      this.currentVariantId = null;
      this.#setCrossSellVisibility(false);
    } else {
      this.currentVariantId = null;
      this.#setCrossSellVisibility(true);
    }
  };

  #setCrossSellVisibility(visible) {
    this.style.display = visible ? 'block' : 'none';

    const wrapper = this.closest('.cross-sell-wrapper');
    if (!wrapper) return;

    wrapper.style.display = visible ? '' : 'none';
  }

  // Met à jour le cross-sell avec la variante correspondante
  #updateCrossSellVariant(variant) { 
var WCP_JSON=[];
      if(document.getElementsByClassName('wcp_json_'+variant.id).length){
        WCP_JSON=JSON.parse(document.getElementsByClassName('wcp_json_'+variant.id)[0].innerHTML);
        for(var tempwcp=0; tempwcp < WCP_JSON.variants.length; tempwcp++){
          if(variant.id==WCP_JSON.variants[tempwcp].id){
            variant.price=WCP_JSON.variants[tempwcp].price;
            variant.compare_at_price=WCP_JSON.variants[tempwcp].compare_at_price;
          }
        }
      }	

    this.currentVariantId = variant.id;

    const image = this.querySelector('[data-ref="cross-sell-image"]');
    if (image && variant.featured_image) {
      image.src = variant.featured_image;
    }

    // Met à jour le lien de l'image avec la variante
    const imageLink = this.querySelector('.cross-sell__image-link');
    if (imageLink) {
      const baseUrl = imageLink.dataset.productUrl || imageLink.href.split('?')[0];
      imageLink.href = `${baseUrl}?variant=${variant.id}`;
    }

    const priceContainer = this.querySelector('.price-container .price');
    const compareAtPriceContainer = this.querySelector('.price-container .compare-at-price');

    if (priceContainer) {
      priceContainer.innerText = formatMoney(variant.price);
    }

    if (compareAtPriceContainer) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareAtPriceContainer.innerText = formatMoney(variant.compare_at_price);
      } else {
        compareAtPriceContainer.innerText = '';
      }
    }

    this.#updateButtonPrices(variant.price, variant.compare_at_price);
  }

  #updateButtonPrices(price, compareAtPrice) {
    const buttonPriceContainer = this.querySelector('[data-ref="add-to-cart-button"] .price-container');
    if (!buttonPriceContainer) return;

    const buttonMainPrice = buttonPriceContainer.querySelector('.price');
    const buttonComparePrice = buttonPriceContainer.querySelector('.compare-at-price');
    if (!buttonMainPrice) return;

    buttonMainPrice.innerText = formatMoney(price);

    if (buttonComparePrice) {
      if (compareAtPrice && compareAtPrice > price) {
        buttonComparePrice.innerText = formatMoney(compareAtPrice);
        return;
      }

      buttonComparePrice.innerText = '';
    }
  }
}

if (!customElements.get('cross-sell-component')) {
  customElements.define('cross-sell-component', CrossSellComponent);
}
